import {
  SelectableFormElement,
  setSelectableFormElementChildItems,
  useFormElement,
  useSelectableFormElement
} from '@common';
import { useIsLoading } from '@core';
import { computed, onMounted, toRef } from 'vue';
import { storeToRefs } from 'pinia';
import { useDecisionSupportWizardStore } from '../stores/decision-support-wizard.store';
import type { DecisionSupportWizardParcelData } from '../models/decision-support-wizard.model';
import { useI18n } from 'vue-i18n';

export const useDecisionSupportWizardParcelCard = (parcel: DecisionSupportWizardParcelData, emit) => {
  const { isLoading } = useIsLoading();

  const decisionSupportWizardStore = useDecisionSupportWizardStore();
  const { decisionSupportData, isCopy } = storeToRefs(decisionSupportWizardStore);

  const parcelItem = toRef(parcel);

  const getForm = computed(() => parcel.form);

  const { t } = useI18n();

  const { getChild, validate } = useFormElement(getForm.value.element);

  const sourceField = getChild('source') as SelectableFormElement;
  const upkeepCostField = getChild('upkeepCost') as SelectableFormElement;
  const cropPriceField = getChild('cropPrice') as SelectableFormElement;

  const { value: sourceValue, items: sourceItems } = useSelectableFormElement(sourceField);
  const { value: upkeepCostValue, settings: upkeepCostSettings } = useFormElement(upkeepCostField);
  const { value: cropPriceValue } = useFormElement(cropPriceField);

  upkeepCostSettings.value.custom = {
    area: parcelItem.value.area,
    translatedText: t('decisionSupport.wizard.label.absoluteValue')
  };

  const sourceMissing = computed(() => {
    if (!decisionSupportData.value.kind) {
      return true;
    }

    const sourceItems = sourceField.items;

    return !sourceItems.length;
  });

  const setSourceSelectItems = async () => {
    if (!decisionSupportData.value.year) {
      return;
    }

    const wizardList = await decisionSupportWizardStore.loadWizardList(parcel.id, decisionSupportData.value.year);

    setSelectableFormElementChildItems('source', mapToSelectableItems(wizardList), getForm.value.element);

    if (isCopy.value) {
      copyMetadataToForm();
    } else {
      chooseProperSourceItem();
    }
  };

  const mapToSelectableItems = (wizardList) => {
    return wizardList.map((wizardItem, index) => ({
      name: wizardItem.name,
      value: index,
      layerGroupId: wizardItem.id,
      layerGroupType: wizardItem.layerGroupType,
      layerGroupName: wizardItem.name
    }));
  };

  const chooseProperSourceItem = () => {
    if (!sourceField.items || sourceField.items.length === 0) {
      return;
    }
    sourceValue.value = sourceField.items[0];
  };

  const copyMetadataToForm = async () => {
    const ownMetadataParcel = decisionSupportData.value.metadata.parcels.find((p) => p.id === parcelItem.value.id);

    if (!ownMetadataParcel) {
      chooseProperSourceItem();
      return;
    }

    upkeepCostValue.value = ownMetadataParcel.upkeepCost;
    cropPriceValue.value = ownMetadataParcel.cropPrice;

    if (!sourceItems.value) {
      return;
    }
    const sourceToCopy = sourceItems.value.find((item) => item.layerGroupId === ownMetadataParcel.layerGroupId);

    sourceField.value = sourceToCopy;

    await validate();
  };

  const zoomToExtent = () => {
    emit('zoomToExtent');
  };

  const removeParcel = () => {
    emit('removeParcel');
  };

  onMounted(async () => {
    isLoading.value = true;
    await setSourceSelectItems();
    await validate();
    isLoading.value = false;
  });

  return {
    form: parcel.form,
    parcelItem,
    isLoading,
    sourceMissing,
    zoomToExtent,
    removeParcel
  };
};
